(() => {
  const BEANY_RETURN_URL_KEY = "beany-product-return-url";
  const prefetchedNavUrls = new Set();

  const prefetchNavigationUrl = (url) => {
    if (!url || prefetchedNavUrls.has(url)) return;
    prefetchedNavUrls.add(url);
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    document.head.appendChild(link);
    if (typeof fetch === "function") {
      fetch(url, { credentials: "same-origin" }).catch(() => {});
    }
  };

  const saveProductReturnUrl = () => {
    try {
      sessionStorage.setItem(BEANY_RETURN_URL_KEY, window.location.href);
    } catch (_error) {
      /* ignore */
    }
  };

  const tierApi = () => window.BeanyBundleTier;

  const REF_CENTS_PER_BAG = 12495;
  const RENEW_DAYS_BY_TIER = {
    1: 30,
    2: 30,
    3: 45,
    4: 60,
  };
  const fallbackSaleByTier = {
    1: 11795,
    2: 19990,
    3: 26985,
    4: 32780,
  };
  const fallbackCompareByTier = {
    1: 12495,
    2: 24990,
    3: 37485,
    4: 49980,
  };
  const fallbackSavingsByTier = {
    1: 6,
    2: 20,
    3: 28,
    4: 34,
  };
  const fallbackRenewByTier = {
    1: 11795,
    2: 20990,
    3: 29985,
    4: 37980,
  };
  const fallbackPricingForTier = (tier) => {
    const sale = fallbackSaleByTier[tier];
    const renew = fallbackRenewByTier[tier];
    const compare = fallbackCompareByTier[tier];
    return {
      sale,
      renew,
      compare,
      savings: fallbackSavingsByTier[tier],
      renewPerBagCents: Math.round(renew / tier),
      perBagCents: Math.round(sale / tier),
    };
  };
  const fallbackTierConfig = {
    1: fallbackPricingForTier(1),
    2: fallbackPricingForTier(2),
    3: fallbackPricingForTier(3),
    4: fallbackPricingForTier(4),
  };

  const money = (cents) => {
    if (tierApi()) return tierApi().money(cents);
    return `₪${(Math.max(0, cents) / 100).toFixed(2)}`;
  };

  const paint = (el, text) => {
    if (!el || text == null || el.textContent === text) return;
    el.textContent = text;
  };

  const perBagCentsForBundleChoice = (minBags) => {
    if (tierApi()) return tierApi().tierFromBagCount(minBags);
    const n = Math.max(1, Number(minBags) || 1);
    return n >= 4 ? 4 : n;
  };

  const purchaseMode = () => {
    const root = document.querySelector("[data-bundle-funnel], [data-step-2-root]");
    // The product offer button is the club deal. A one-time choice in the cart
    // drawer must not rewrite it.
    if (
      document.body?.classList?.contains("template-product") &&
      root?.dataset?.purchaseMode !== "one_time"
    ) {
      return "club";
    }
    if (window.__beanyPurchaseMode === "one_time" || window.__beanyPurchaseMode === "club") {
      return window.__beanyPurchaseMode;
    }
    try {
      const stored = sessionStorage.getItem("beany-purchase-mode");
      if (stored === "one_time" || stored === "club") return stored;
    } catch (_err) {
      /* ignore */
    }
    if (root?.dataset?.purchaseMode === "one_time") return "one_time";
    return "club";
  };

  const getPricing = (finalBags) => {
    const mode = purchaseMode();
    if (tierApi()) {
      const p = tierApi().getPricingForBags(finalBags, mode);
      return {
        sale: p.sale,
        compare: p.compare,
        savings: p.savings,
        renew: p.renew,
        perBagCents: p.perBagCents,
        perBagText: p.perBagText,
        renewPerBagCents: p.renewPerBagCents,
        renewPerBagText: p.renewPerBagText,
        giftValueCents: p.giftValueCents,
        context: p.context,
        selectedPrice: p.saleDecimal,
        selectedComparePrice: p.compareDecimal,
        mode: p.mode,
      };
    }
    const tier = perBagCentsForBundleChoice(finalBags);
    const pricing = fallbackTierConfig[tier];
    const oneTimeGifts = Number(finalBags) >= 2 ? 7575 : 1990;
    const clubGifts = Number(finalBags) >= 3 ? 11570 : Number(finalBags) >= 2 ? 7575 : 3980;
    return {
      sale: pricing.sale,
      compare: pricing.compare,
      savings: pricing.savings,
      renew: pricing.renew,
      renewPerBagCents: pricing.renewPerBagCents,
      renewPerBagText: money(pricing.renewPerBagCents),
      giftValueCents: mode === "one_time" ? oneTimeGifts : clubGifts,
      selectedPrice: (pricing.sale / 100).toFixed(2),
      selectedComparePrice: (pricing.compare / 100).toFixed(2),
      mode,
    };
  };

  class BundleBuilder {
    constructor(quantityBreaksElement) {
      this.quantityBreaksElement = quantityBreaksElement;
      this.sectionId = quantityBreaksElement.dataset.sectionId;
      this.productFormId = quantityBreaksElement.dataset.productFormId;

      this.funnelRoot = document.querySelector(
        `[data-bundle-funnel][data-section-id="${this.sectionId}"]`
      );
      this.offerCtaOldEls = Array.from(
        this.funnelRoot?.querySelectorAll("[data-offer-cta-old]") || []
      );
      this.offerCtaNewEls = Array.from(
        this.funnelRoot?.querySelectorAll("[data-offer-cta-new]") || []
      );
      this.openFlavorButtons = Array.from(
        this.funnelRoot?.querySelectorAll("[data-open-flavor-step]") || []
      );
      this.stickyAtcEl = this.funnelRoot?.querySelector("[data-bundle-funnel-sticky-atc]");
      this.stickyGiftsCountEl = this.stickyAtcEl?.querySelector("[data-sticky-gifts-count]");
      this.stickySavingTextEl = this.stickyAtcEl?.querySelector("[data-sticky-saving-text]");
      this.ctaAnchorEl = this.funnelRoot?.querySelector("[data-bundle-funnel-cta-anchor]");
      this.renewNoteEls = Array.from(
        this.funnelRoot?.querySelectorAll("[data-beany-subscription-renew-note]") || []
      );
      this.renewPerBagEls = Array.from(
        this.funnelRoot?.querySelectorAll("[data-beany-renew-per-bag]") || []
      );
      this.onetimePriceEls = Array.from(
        this.funnelRoot?.querySelectorAll("[data-beany-onetime-price]") || []
      );
      this.onetimeLinks = Array.from(
        this.funnelRoot?.querySelectorAll("[data-beany-onetime-link]") || []
      );
      this.perCupPromoRoot = this.funnelRoot?.querySelector("[data-beany-per-cup-promo]");
      this.perCupPriceEl = this.funnelRoot?.querySelector("[data-beany-per-cup-price]");
      this.quantityInputs = Array.from(
        this.quantityBreaksElement.querySelectorAll(
          `input[name="quantity"][form="${this.productFormId}"][data-bundle-choice="true"]`
        )
      );
      this.liveFinalBags = null;
      this.lastBroadcastFinalBags = null;

      this.bindEvents();
      this.ensureCheckedChoice();
      this.updateOfferStepCta();
      this.initStickyAtcVisibility();
    }

    bindEvents() {
      this.quantityInputs.forEach((input) => {
        input.addEventListener("change", () => this.updateOfferStepCta());
      });
      this.quantityBreaksElement.addEventListener("change", () => this.updateOfferStepCta());
      this.quantityBreaksElement.addEventListener("click", (event) => {
        if (!event.target.closest('label.quantity-break, input[name="quantity"][data-bundle-choice="true"]'))
          return;
        window.requestAnimationFrame(() => this.updateOfferStepCta());
      });

      document.addEventListener("bundle:state-change", (event) => {
        if (event.detail?.source === "bundle-builder") return;
        const sourceSection = event.detail?.sectionId || event.target?.dataset?.sectionId;
        if (sourceSection && String(sourceSection) !== String(this.sectionId)) return;
        const minimumRequiredBags = Number(event.detail?.minimumRequiredBags || 1);
        const actualSelectedBags = Number(event.detail?.actualSelectedBags || 0);
        this.liveFinalBags = Math.max(minimumRequiredBags, actualSelectedBags, 1);
        this.updateOfferStepCta();
      });

      this.openFlavorButtons.forEach((button) => {
        const warm = () => this.warmFlavorStepPrefetch();
        button.addEventListener("mouseenter", warm, { passive: true });
        button.addEventListener("focus", warm);
        button.addEventListener("touchstart", warm, { passive: true, capture: true });
        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.navigateToFlavorStep();
        });
      });

      this.warmFlavorStepPrefetch();
    }

    getStep2BaseUrl() {
      return (
        this.funnelRoot?.dataset.step2Url ||
        document.querySelector("[data-beany-bundle-root]")?.dataset.step2Url ||
        "/pages/שלב-2-בחירת-טעמים"
      );
    }

    buildFlavorStepUrl(selection) {
      const { selectedBags, selectedPrice, selectedComparePrice } = selection;
      const step2Base = this.getStep2BaseUrl();
      const boost =
        window.BEANY_BOOST_OFFER === true ||
        document.documentElement.classList.contains("beany-boost") ||
        document.body?.classList?.contains("template-suffix-boost") ||
        tierApi()?.isBoostOfferContext?.() === true;
      return (
        `${step2Base}?bags=${selectedBags}` +
        `&price=${selectedPrice}` +
        `&compare=${selectedComparePrice}` +
        (boost ? `&beany_boost=1` : "")
      );
    }

    warmFlavorStepPrefetch() {
      const selection = this.getSelectedBundlePricing();
      if (!selection) return;
      prefetchNavigationUrl(this.buildFlavorStepUrl(selection));
    }

    navigateToFlavorStep() {
      const selection = this.getSelectedBundlePricing();
      if (!selection) return;
      saveProductReturnUrl();
      window.location.assign(this.buildFlavorStepUrl(selection));
    }

    initStickyAtcVisibility() {
      if (!this.stickyAtcEl) return;

      const getMidPageScrollThreshold = () => {
        const scrollHeight = Math.max(
          document.documentElement.scrollHeight,
          document.body.scrollHeight
        );
        const viewportHeight = window.innerHeight;
        const maxScroll = Math.max(0, scrollHeight - viewportHeight);
        return maxScroll * 0.5;
      };

      const setStickyVisible = (visible) => {
        this.stickyAtcEl.classList.toggle("is-visible", visible);
        this.stickyAtcEl.setAttribute("aria-hidden", visible ? "false" : "true");
        document.documentElement.style.setProperty(
          "--sticky-atc-offset",
          visible ? `${this.stickyAtcEl.offsetHeight}px` : "0px"
        );
      };

      const onScroll = () => {
        const pastMidPage = window.scrollY >= getMidPageScrollThreshold();
        setStickyVisible(pastMidPage);
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      onScroll();
    }

    ensureCheckedChoice() {
      const checked = this.quantityInputs.find((input) => input.checked) || this.quantityInputs[0];
      if (checked && !checked.checked) checked.checked = true;
    }

    parseMinimum(input) {
      const minBags = Number(input?.dataset.minBags || input?.value || 1);
      return Number.isFinite(minBags) && minBags > 0 ? minBags : 1;
    }

    getSelectedBundleInput() {
      const checkedInsideRoot = this.quantityBreaksElement.querySelector(
        'input[name="quantity"][data-bundle-choice="true"]:checked'
      );
      if (checkedInsideRoot) return checkedInsideRoot;
      return this.quantityInputs.find((input) => input.checked) || this.quantityInputs[0] || null;
    }

    getSelectedBundleBags() {
      const input = this.getSelectedBundleInput();
      return this.parseMinimum(input);
    }

    getGiftsOpenedAndTotal(finalBags) {
      const slots = Array.from(this.funnelRoot?.querySelectorAll("[data-gift-slot]") || []);
      if (slots.length) {
        let opened = 0;
        slots.forEach((slot) => {
          const raw = slot.dataset.giftUnlockAt;
          const need = raw !== undefined && raw !== "" ? Number(raw) : 99;
          const safeNeed = Number.isFinite(need) ? need : 99;
          if (safeNeed > 0 && finalBags >= safeNeed) opened += 1;
        });
        return { opened, total: slots.length };
      }

      const bags = Math.max(1, Number(finalBags) || 1);
      return {
        opened: bags >= 3 ? 5 : bags >= 2 ? 4 : 2,
        total: 5,
      };
    }

    getSavingsAmount(finalBags, pricing) {
      const p = pricing || getPricing(finalBags);
      return Math.round(Math.max(0, (Number(p?.compare) || 0) - (Number(p?.sale) || 0)) / 100);
    }

    getTotalSavePercent(pricing) {
      const valueCents = Number(pricing?.compare || 0) + Number(pricing?.giftValueCents || 0);
      return valueCents > 0
        ? Math.max(0, Math.round(((valueCents - Number(pricing?.sale || 0)) / valueCents) * 100))
        : 0;
    }

    updateStickySummary(finalBags, pricing) {
      if (!this.stickyAtcEl) return;

      if (this.stickySavingTextEl) {
        this.stickySavingTextEl.textContent = `חוסכים ${this.getTotalSavePercent(pricing || getPricing(finalBags))}%`;
      }

      const { opened, total } = this.getGiftsOpenedAndTotal(finalBags);
      if (this.stickyGiftsCountEl) {
        const totalEl = this.stickyGiftsCountEl.querySelector("[data-gifts-total]");
        const openedEl = this.stickyGiftsCountEl.querySelector("[data-gifts-opened]");
        if (totalEl && openedEl) {
          totalEl.textContent = String(total);
          openedEl.textContent = String(opened);
        } else {
          this.stickyGiftsCountEl.textContent = `${total}/${opened}`;
        }
      }
    }

    getRenewDaysForBags(bags) {
      const tier = perBagCentsForBundleChoice(bags);
      return RENEW_DAYS_BY_TIER[tier] ?? 30;
    }

    getRenewBagsLabel(bags) {
      const max = window.BeanyBundleTier?.MAX_BAGS || 6;
      const n = Math.max(1, Math.min(max, Number(bags) || 1));
      return n === 1 ? "שקית 1" : `${n} שקיות`;
    }

    updateSubscriptionRenewNote(finalBags, pricing) {
      const days = this.getRenewDaysForBags(finalBags);
      const savingsText = String(this.getSavingsAmount(finalBags, pricing));
      const totalSavePercent = this.getTotalSavePercent(pricing);

      this.renewNoteEls.forEach((note) => {
        const daysEl = note.querySelector("[data-renew-days]");
        const saveEl = note.querySelector("[data-renew-save-percent]");
        const totalSaveEl = note.querySelector("[data-renew-save-total-percent]");
        const giftEl = note.querySelector("[data-renew-gift-value]");
        if (daysEl) daysEl.textContent = String(days);
        if (saveEl) saveEl.textContent = savingsText;
        if (totalSaveEl) totalSaveEl.textContent = String(totalSavePercent);
        if (giftEl && pricing?.giftValueCents != null) giftEl.textContent = money(pricing.giftValueCents);
      });

      this.funnelRoot?.querySelectorAll("[data-beany-first-order-savings]").forEach((el) => {
        el.textContent = savingsText;
      });

      const shippingText =
        Number(finalBags) >= 2
          ? "משלוח חינם עד דלת הבית בכל הזמנה"
          : "משלוח עד דלת הבית במחיר מוזל של 19.90";
      this.funnelRoot?.querySelectorAll("[data-beany-product-shipping-perk]").forEach((el) => {
        const item = el.closest(".beany-subscription-renew-note__item");
        if (item) item.hidden = false;
        el.textContent = shippingText;
      });
    }

    updateRenewPerBagPrice(pricing, finalBags) {
      const renewText =
        pricing?.renewPerBagText ||
        (pricing?.renewPerBagCents != null ? money(pricing.renewPerBagCents) : null);
      if (renewText && this.renewPerBagEls.length) {
        this.renewPerBagEls.forEach((el) => paint(el, renewText));
      }

      const firstPerBagCents = Math.round(
        Number(pricing?.sale) / Math.max(1, Number(finalBags) || 1)
      );
      const renewPerBagCents = Number(pricing?.renewPerBagCents);
      const renewUnchanged =
        Number.isFinite(firstPerBagCents) && Number.isFinite(renewPerBagCents)
          ? Math.abs(firstPerBagCents - renewPerBagCents) <= 1
          : Number(finalBags) === 1;
      this.funnelRoot?.querySelectorAll("[data-beany-renew-verb]").forEach((el) => {
        el.textContent = renewUnchanged ? "נשאר ב" : "מתעדכן ל";
      });

      const days = this.getRenewDaysForBags(finalBags);
      const bagsLabel = this.getRenewBagsLabel(finalBags);
      this.funnelRoot?.querySelectorAll("[data-beany-renew-perk]").forEach((perk) => {
        const daysEl = perk.querySelector("[data-renew-days]");
        const bagsEl = perk.querySelector("[data-renew-bags-label]");
        if (daysEl) daysEl.textContent = String(days);
        if (bagsEl) bagsEl.textContent = bagsLabel;
      });
    }

    updateOnetimeLinkPrice() {
      const bags = 1;
      const api = tierApi();
      const onetimePricing = api ? api.getPricingForBags(bags, "one_time") : null;
      const fallbackOnetimeByTier = { 1: 12495, 2: 21990, 3: 31485, 4: 37980 };
      const priceText =
        onetimePricing?.perBagText ||
        onetimePricing?.saleText ||
        money(fallbackOnetimeByTier[1]);
      const boost =
        window.BEANY_BOOST_OFFER === true ||
        api?.isBoostOfferContext?.() === true ||
        document.documentElement.classList.contains("beany-boost");

      if (this.onetimePriceEls?.length) {
        this.onetimePriceEls.forEach((el) => {
          el.textContent = priceText;
        });
      }

      if (this.onetimeLinks?.length) {
        this.onetimeLinks.forEach((link) => {
          try {
            const url = new URL(
              link.getAttribute("href") || "/pages/קניה-חד-פעמית",
              window.location.origin
            );
            url.searchParams.set("bags", "1");
            if (boost) url.searchParams.set("beany_boost", "1");
            else url.searchParams.delete("beany_boost");
            link.setAttribute("href", `${url.pathname}${url.search}`);
          } catch (_) {
            link.setAttribute(
              "href",
              boost
                ? `/pages/קניה-חד-פעמית?bags=1&beany_boost=1`
                : `/pages/קניה-חד-פעמית?bags=1`
            );
          }
        });
      }
    }

    getPerCupText(finalBags, pricing) {
      const bags = Math.max(1, Number(finalBags) || 1);
      if (pricing?.perCupText) return pricing.perCupText;
      const perBag = Number.isFinite(Number(pricing?.perBagCents))
        ? Number(pricing.perBagCents)
        : Math.round(Number(pricing?.sale) / bags);
      if (!Number.isFinite(perBag) || perBag <= 0) return "";
      return money(Math.round(perBag / 20));
    }

    updatePerCupPromo(finalBags, pricing) {
      if (!this.perCupPriceEl) return;
      const perCupText = this.getPerCupText(finalBags, pricing);
      if (!perCupText) return;
      paint(this.perCupPriceEl, perCupText);
      if (this.perCupPromoRoot) {
        this.perCupPromoRoot.setAttribute("aria-label", `רק ${perCupText} לכוס קפה קר מפנק!`);
      }
    }

    updateOfferStepCta() {
      if (!this.quantityInputs?.length) return;
      const minBags = this.getSelectedBundleBags();
      const onProductOffer = document.body?.classList?.contains("template-product");
      const finalBags = onProductOffer
        ? minBags
        : Math.max(minBags, Number(this.liveFinalBags || 0));
      const pricing = getPricing(finalBags);

      this.updateStickySummary(finalBags, pricing);
      this.updateSubscriptionRenewNote(finalBags, pricing);
      this.updateRenewPerBagPrice(pricing, finalBags);
      this.updatePerCupPromo(finalBags, pricing);
      this.updateOnetimeLinkPrice();
      this.warmFlavorStepPrefetch();

      if (!this.offerCtaOldEls.length || !this.offerCtaNewEls.length) return;

      const compareText = money(pricing.compare);
      const saleText = money(pricing.sale);
      const hasDiscount = Number(pricing.compare) > Number(pricing.sale);
      this.offerCtaOldEls.forEach((el) => {
        paint(el, compareText);
        el.hidden = !hasDiscount;
      });
      this.offerCtaNewEls.forEach((el) => {
        paint(el, saleText);
      });

      if (this.lastBroadcastFinalBags !== finalBags) {
        this.lastBroadcastFinalBags = finalBags;
        document.dispatchEvent(
          new CustomEvent("bundle:state-change", {
            detail: {
              sectionId: this.sectionId,
              minimumRequiredBags: minBags,
              actualSelectedBags: finalBags,
              source: "bundle-builder",
            },
          })
        );
      }
    }

    getSelectedBundlePricing() {
      const selectedBags = this.getSelectedBundleBags();
      const onProductOffer = document.body?.classList?.contains("template-product");
      const finalBags = onProductOffer
        ? selectedBags
        : Math.max(selectedBags, Number(this.liveFinalBags || 0));
      const pricing = getPricing(finalBags);

      return {
        selectedBags: finalBags,
        selectedPrice: pricing.selectedPrice,
        selectedComparePrice: pricing.selectedComparePrice,
      };
    }
  }

  const initBundleBuilders = () => {
    const roots = document.querySelectorAll("[data-bundle-builder-root='true']");
    roots.forEach((root) => new BundleBuilder(root));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initBundleBuilders);
  } else {
    initBundleBuilders();
  }
})();
